import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Find Winning Products With Sugargoo Spreadsheet",
  description: "Learn the proven methodology for identifying trending, profitable products using the Sugargoo Spreadsheet before your competitors discover them.",
  image: "https://readdy.ai/api/search-image?query=Fashion%20trend%20research%20workspace%20with%20magazine%20cutouts%20fabric%20swatches%20product%20photos%20and%20analytics%20charts%20on%20clean%20desk%20creative%20mood%20board%20natural%20lighting%20modern%20design%20studio%20aesthetic%20minimal%20editorial%20style&width=1400&height=700&seq=winning-products-guide&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function WinningProducts() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">How to Find Winning Products</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Strategy</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                How to Find Winning Products With Sugargoo Spreadsheet
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                Learn the proven methodology for identifying trending, profitable products using the Sugargoo Spreadsheet before your competitors discover them.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 7 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Fashion%20trend%20research%20workspace%20with%20magazine%20cutouts%20fabric%20swatches%20product%20photos%20and%20analytics%20charts%20on%20clean%20desk%20creative%20mood%20board%20natural%20lighting%20modern%20design%20studio%20aesthetic%20minimal%20editorial%20style&width=1400&height=700&seq=winning-products-guide&orientation=landscape" alt="How to Find Winning Products With Sugargoo Spreadsheet" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">What Makes a Winning Product?</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                A winning product is not just something that looks good — it is an item with proven market demand, reasonable sourcing costs, manageable shipping logistics, and sufficient profit margin. Using the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link>, you can systematically evaluate products against these criteria rather than relying on gut feelings or random browsing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { title: "Market Demand", desc: "Look for products with consistent search interest and social media engagement. Trending items on TikTok or Instagram that have not yet saturated the market are prime candidates." },
                  { title: "Profit Margin", desc: "Aim for at least 3x markup from total landed cost. The spreadsheet helps you calculate this by providing both product prices and shipping weight estimates." },
                  { title: "Shipping Viability", desc: "Lightweight, compact items ship cheaper and arrive faster. Avoid bulky or fragile products unless the margins justify the higher shipping costs and breakage risk." },
                  { title: "Supplier Reliability", desc: "Only source from suppliers with consistent ratings and positive feedback in the spreadsheet. A great product from an unreliable supplier is a recipe for customer complaints." },
                ].map((f, i) => (
                  <div key={i} className="p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{f.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">The Product Research Workflow</h2>
              <div className="space-y-3">
                {[
                  "Scan social media platforms (TikTok, Instagram, Pinterest) to identify emerging trends and product categories gaining traction.",
                  "Cross-reference trending products with the Sugargoo Spreadsheet to see if similar items are available at competitive prices.",
                  "Filter by category and price range to narrow down to products that match your target market and budget constraints.",
                  "Compare at least 5 suppliers for each product type — note differences in minimum order quantities, pricing tiers, and shipping weights.",
                  "Calculate total landed cost including product price, domestic China shipping, international shipping, and any customs duties.",
                  "Test market demand with a small batch order before scaling up. Use the initial sales data to validate or adjust your product selection.",
                ].map((step, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 mt-0.5">
                      <span className="text-xs font-bold text-primary-500">{i + 1}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Find Your Next Winning Product</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Get the Sugargoo Spreadsheet and start discovering profitable products with a proven research system.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/sugargoo-spreadsheet-complete-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Complete Guide 2026</Link></li>
                <li><Link to="/blog/sugargoo-spreadsheet-for-beginners" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet for Beginners</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}